import { ReactNode } from "react";
import { SiteLayout } from "./SiteLayout";
import { ContentBlock } from "./ContentBlock";
import { Breadcrumbs, type Crumb } from "@/components/Breadcrumbs";

type Props = {
  eyebrow?: string;
  title: string;
  description?: string;
  /**
   * Breadcrumb trail rendered at the top of the page header, above the
   * eyebrow and title. Pages should always pass this if they sit deeper
   * than the homepage — it's the standard orientation cue and the only
   * place on the page where the trail belongs visually.
   */
  crumbs?: Crumb[];
  /** The working tool (or a hub's directory), rendered right under the header. */
  children?: ReactNode;
  /**
   * All explanatory content, rendered inside the single #mv-content block
   * after the tool. FAQ goes last. See ContentBlock for the allowed markup.
   */
  content: ReactNode;
  /** Related links, rendered after #mv-content. */
  related?: ReactNode;
  /** Extra sidebar content (e.g. a guide TOC) above the #mv-sidebar ad slot. */
  aside?: ReactNode;
  /** Kept for callers; every page now uses the wide column. */
  wide?: boolean;
  /**
   * Hide the content block and related links when printing, for tools that
   * print their own output (label sheets) with window.print().
   */
  hideContentInPrint?: boolean;
};

export function ToolLayout({
  eyebrow,
  title,
  description,
  crumbs,
  children,
  content,
  related,
  aside,
  wide,
  hideContentInPrint,
}: Props) {
  // One width for every page with a sidebar: from 1100px the content column
  // is at least 728px next to a 300px sidebar (Journey minimums).
  void wide;
  const width = "max-w-6xl";
  return (
    <SiteLayout>
      <section className="border-b border-border bg-muted/30">
        <div className={`mx-auto ${width} px-4 pb-8 pt-5 md:pb-10 md:pt-6`}>
          <div>
            {crumbs && crumbs.length > 0 && (
              <div className="mb-4 text-sm">
                <Breadcrumbs items={crumbs} />
              </div>
            )}
            {eyebrow && (
              <div className="mb-3 inline-flex items-center rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
                {eyebrow}
              </div>
            )}
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">{title}</h1>
            {description && (
              <p className="mt-3 max-w-3xl text-base text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
      </section>
      <div className={`mx-auto ${width} px-4 pb-10 pt-6 md:pt-8`}>
        {children}
        <div className={hideContentInPrint ? "print:hidden" : undefined}>
          {/* Block below 1100px; from 1100px the content column (728px or
              more) sits next to a 300px sidebar. Journey's sidebar rules: the
              container must be static, at least 300px wide and visible from
              1100px; Journey makes its own ads sticky inside it.
              minmax(0,1fr) lets wide tables scroll inside the content column. */}
          <div className="min-[1100px]:grid min-[1100px]:grid-cols-[minmax(0,1fr)_300px] min-[1100px]:gap-10">
            <ContentBlock>{content}</ContentBlock>
            <aside className="hidden pt-12 min-[1100px]:block">
              {aside}
              <div id="mv-sidebar" />
            </aside>
          </div>
          {/* Where Journey's sidebar ad rail stops. Journey sizes the rail as
              the sidebar height plus the distance to the LAST element matching
              its stop selector (default ".site-footer, ..., footer:not(.entry-
              footer)"). The page footer carries "entry-footer", so this marker
              is that element and the rail ends with the article instead of
              leaving empty space after the FAQ. */}
          <div id="mv-sidebar-stop" className="site-footer" aria-hidden="true" />
          {related}
        </div>
      </div>
    </SiteLayout>
  );
}
